import React, { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';

interface HashtagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
  placeholder?: string;
  readonlyTags?: string[];
  rejectedTags?: string[];
  statusTags?: Array<{ tag: string; status: 'Approved' | 'Rejected' | 'Pending' }>;
}

export const HashtagInput: React.FC<HashtagInputProps> = ({
  value,
  onChange,
  maxTags = 10,
  placeholder = 'Type a tag and press Enter',
  readonlyTags = [],
  rejectedTags = [],
  statusTags = [],
}) => {
  const [input, setInput] = useState('');

  const addTag = (raw: string) => {
    const tag = raw.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag || value.includes(tag) || value.length >= maxTags) return;
    onChange([...value, tag]);
    setInput('');
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    }
    if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--color-gray-200)',
        borderRadius: 'var(--radius-lg)',
        padding: '10px 12px',
        minHeight: '52px',
        background: 'var(--color-white)',
        cursor: 'text',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          marginBottom: value.length ? 4 : 0,
        }}
      >
        {statusTags.map((row) => (
          <span
            key={`status-${row.status}-${row.tag}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background:
                row.status === 'Approved'
                  ? 'rgba(34,197,94,0.12)'
                  : row.status === 'Rejected'
                  ? 'rgba(239,68,68,0.12)'
                  : 'rgba(234,179,8,0.14)',
              color:
                row.status === 'Approved'
                  ? '#16a34a'
                  : row.status === 'Rejected'
                  ? '#dc2626'
                  : '#f59e0b',
              border:
                row.status === 'Approved'
                  ? '1px solid rgba(34,197,94,0.35)'
                  : row.status === 'Rejected'
                  ? '1px solid rgba(239,68,68,0.35)'
                  : '1px solid rgba(234,179,8,0.35)',
              borderRadius: 'var(--radius-full)',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            #{row.tag}
          </span>
        ))}
        {statusTags.length === 0 && readonlyTags.map((tag) => (
          <span
            key={`readonly-${tag}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(34,197,94,0.12)',
              color: '#16a34a',
              border: '1px solid rgba(34,197,94,0.35)',
              borderRadius: 'var(--radius-full)',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            #{tag}
          </span>
        ))}
        {statusTags.length === 0 && rejectedTags.map((tag) => (
          <span
            key={`rejected-${tag}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(239,68,68,0.12)',
              color: '#dc2626',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 'var(--radius-full)',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            #{tag}
          </span>
        ))}
        {value.map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'var(--color-primary-50)',
              color: 'var(--color-primary)',
              borderRadius: 'var(--radius-full)',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-primary)',
                padding: 0,
                lineHeight: 1,
              }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      {value.length < maxTags && (
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => input && addTag(input)}
          placeholder={
            value.length === 0 ? placeholder : 'Add another tag...'
          }
          style={{
            border: 'none',
            outline: 'none',
            width: '100%',
            minHeight: '28px',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-gray-800)',
            background: 'transparent',
          }}
        />
      )}
      <p
        style={{
          fontSize: '11px',
          color: 'var(--color-gray-400)',
          marginTop: 6,
          lineHeight: 1.4,
        }}
      >
        Press Enter or comma to add • {value.length}/{maxTags} tags
      </p>
    </div>
  );
};

export default HashtagInput;
